// @ts-nocheck
/*
 * Port of manage-webhook/create.js (1323 lines). BYTE-IDENTICAL.
 * One component drives both Create and Edit â€” isEditMode is derived from
 * useParams().id. Edit mode fetches an existing webhook (Sequelize model
 * â†’ `.dataValues`), parses stored `header_json` back into a rows table
 * (Content-Type row is `protected` â€” cannot be edited/deleted), and lazily
 * merges the persisted `body_json` into the fetched request-parameters
 * list so previously-selected rows are pre-checked and their values are
 * restored (with the `useTemplateBraces` flag inferred from whether the
 * stored value was already wrapped in `{ }`). Route switch from
 * MANAGE_WEBHOOK.EDIT â†’ MANAGE_WEBHOOK.CREATE clears local state so the
 * same component acts as a fresh form.
 * Endpoints: API_WEBHOOK_CREATE / UPDATE / GET_ONE / REQUEST_PARAMETERS.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Card as ShadCard,
  CardContent as ShadCardContent,
  CardHeader as ShadCardHeader,
} from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import { Input as ShadInput } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Container: any = ({ fluid, className, children, ...props }: any) => (
  <div className={_cx(fluid ? 'container-fluid' : 'container', className)} {...props}>
    {children}
  </div>
);
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <ShadCardHeader className={_cx('font-semibold', className)} {...props}>
    {children}
  </ShadCardHeader>
);
Card.Body = ({ className, children, ...props }: any) => (
  <ShadCardContent className={className} {...props}>
    {children}
  </ShadCardContent>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('text-base font-semibold', className)} {...props}>
    {children}
  </As>
);
Card.Text = ({ className, children, ...props }: any) => (
  <p className={_cx('text-sm text-muted-foreground', className)} {...props}>
    {children}
  </p>
);
Card.Footer = ({ className, children, ...props }: any) => (
  <div className={_cx('border-t border-border px-6 py-3', className)} {...props}>
    {children}
  </div>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('space-y-2 mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label
    className={_cx('text-sm font-medium leading-none', className)}
    htmlFor={htmlFor}
    {...props}
  >
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const invalidCls = isInvalid ? 'border-destructive focus-visible:ring-destructive' : '';
  if (as === 'select')
    return (
      <select
        className={_cx(
          'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          invalidCls,
          className,
        )}
        {...props}
      >
        {children}
      </select>
    );
  if (as === 'textarea')
    return (
      <textarea
        className={_cx(
          'flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          invalidCls,
          className,
        )}
        {...props}
      />
    );
  return <ShadInput className={_cx(invalidCls, className)} {...props} />;
};
Form.Control.Feedback = ({ type: _t, className, children, ...props }: any) => (
  <div className={_cx('text-xs text-destructive', className)} {...props}>
    {children}
  </div>
);
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx(
      'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      isInvalid && 'border-destructive focus-visible:ring-destructive',
      className,
    )}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('flex items-center gap-2', inline && 'inline-flex mr-3', className)}>
    <input
      type={type}
      id={id}
      className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
      {...props}
    />
    {label !== undefined && (
      <label htmlFor={id} className="text-sm leading-none">
        {label}
      </label>
    )}
    {children}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small
    className={_cx('text-xs', muted && 'text-muted-foreground', className)}
    {...props}
  >
    {children}
  </small>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
const Table: any = ({
  bordered: _b,
  striped: _s,
  hover: _h,
  responsive: _r,
  className,
  children,
  ...props
}: any) => (
  <table className={_cx('w-full text-sm', className)} {...props}>
    {children}
  </table>
);
const _TabsCtx = React.createContext<any>({});
const Tabs: any = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  children,
  className,
  ...props
}: any) => {
  const [internalKey, setInternalKey] = React.useState(defaultActiveKey || null);
  const current = activeKey !== undefined ? activeKey : internalKey;
  const handle = (k: any) => {
    if (activeKey === undefined) setInternalKey(k);
    onSelect?.(k);
  };
  const tabs = React.Children.toArray(children).filter(Boolean) as any[];
  if (current === null && tabs.length) {
    setInternalKey(tabs[0]?.props?.eventKey);
  }
  return (
    <_TabsCtx.Provider value={{ current, setKey: handle }}>
      <div className={className} {...props}>
        <ul className="nav nav-tabs" role="tablist">
          {tabs.map((t: any) => (
            <li key={t.props.eventKey} className="nav-item" role="presentation">
              <button
                type="button"
                className={_cx('nav-link', current === t.props.eventKey && 'active')}
                onClick={() => handle(t.props.eventKey)}
                disabled={t.props.disabled}
              >
                {t.props.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="tab-content">
          {tabs.map((t: any) => (
            <div
              key={t.props.eventKey}
              className={_cx('tab-pane', current === t.props.eventKey && 'active show')}
              style={{ display: current === t.props.eventKey ? undefined : 'none' }}
            >
              {t.props.children}
            </div>
          ))}
        </div>
      </div>
    </_TabsCtx.Provider>
  );
};
const Tab: any = () => null;
const Tooltip: any = ({ children, id: _id, ...props }: any) => (
  <span {...props}>{children}</span>
);
const OverlayTrigger: any = ({ children, overlay, placement: _p, trigger: _t }: any) => {
  let title = '';
  if (overlay?.props?.children) {
    title =
      typeof overlay.props.children === 'string'
        ? overlay.props.children
        : Array.isArray(overlay.props.children)
          ? overlay.props.children.filter((c: any) => typeof c === 'string').join(' ')
          : '';
  }
  return React.cloneElement(children, { title });
};
const Badge: any = ({ bg, pill, className, children, ...props }: any) => {
  const bgMap: Record<string, string> = {
    primary: 'bg-primary text-primary-foreground',
    secondary: 'bg-secondary text-secondary-foreground',
    success: 'bg-emerald-600 text-white',
    danger: 'bg-destructive text-destructive-foreground',
    warning: 'bg-amber-500 text-white',
    info: 'bg-sky-500 text-white',
    light: 'bg-muted text-muted-foreground',
    dark: 'bg-foreground text-background',
  };
  return (
    <span
      className={_cx(
        'inline-flex items-center px-2 py-0.5 text-xs font-medium',
        pill ? 'rounded-full' : 'rounded-md',
        bg && bgMap[bg],
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
};
const Spinner: any = ({ animation: _a, size, variant, className, ...props }: any) => (
  <span
    className={_cx(
      'spinner-border',
      size === 'sm' && 'spinner-border-sm',
      variant && `text-${variant}`,
      className,
    )}
    role="status"
    aria-hidden="true"
    {...props}
  />
);
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiCopy } from 'react-icons/fi';
import { ArrowLeft, Pencil, Trash2, Copy, Loader2, Save, Plus } from 'lucide-react';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { postApiCall, getApiCall } from '@/service/httpCall';
import {
  API_WEBHOOK_CREATE,
  API_WEBHOOK_REQUEST_PARAMETERS,
  API_WEBHOOK_UPDATE,
  API_WEBHOOK_GET_ONE,
} from '@/service/url';
import { ROUTES, FLASH_MESSAGE_ACTION_OBJECT, MODULE_ACL } from '@/utilities/constant';
import Loader from '@/components/partials/components/Loader';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { fetchAndSetFlashMessage } from '@/service/fetchFlashMessage';

const CreateManageWebhook = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = !!id;
  const previousIdRef = useRef<any>(undefined);
  const [loading, setLoading] = useState(false);
  const [loadingParams, setLoadingParams] = useState(true);
  const [loadingWebhook, setLoadingWebhook] = useState(isEditMode);
  const [alertMessage, setAlertMessage] = useState<any>(null);

  // Webhook details state
  const [webhookData, setWebhookData] = useState<any>({
    webhook_name: '',
    url: '',
    event: '',
    is_default: false,
  });

  // Error states for real-time validation
  const [webhookNameError, setWebhookNameError] = useState('');
  const [webhookUrlError, setWebhookUrlError] = useState('');
  const [headerParamError, setHeaderParamError] = useState('');
  const [headerValueError, setHeaderValueError] = useState('');
  const [bodyParamError, setBodyParamError] = useState('');
  const [bodyValueError, setBodyValueError] = useState('');

  // Headers state
  const [headerParam, setHeaderParam] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [headers, setHeaders] = useState<any[]>([
    { parameter: 'Content-Type', value: 'application/json', protected: true },
  ]);
  const [editingHeaderIndex, setEditingHeaderIndex] = useState<any>(null);
  const [editHeaderParam, setEditHeaderParam] = useState('');
  const [editHeaderValue, setEditHeaderValue] = useState('');

  // Body state
  const [bodyParam, setBodyParam] = useState('');
  const [bodyValue, setBodyValue] = useState('');
  const [bodyParams, setBodyParams] = useState<any[]>([]);
  const [editingIndex, setEditingIndex] = useState<any>(null);
  const [editParam, setEditParam] = useState('');
  const [webhookBodyData, setWebhookBodyData] = useState<any>(null);
  const [hasMergedBodyData, setHasMergedBodyData] = useState(false);

  // Fetch request parameters from backend
  useEffect(() => {
    // Guards against React StrictMode / remount double-invoking this effect: without it,
    // a second (duplicate) response can resolve after the body-data merge has already run
    // and silently overwrite the merged `checked`/value state with the unmerged defaults.
    let cancelled = false;
    setLoadingParams(true);
    getApiCall(API_WEBHOOK_REQUEST_PARAMETERS, {})
      .then((response: any) => {
        if (cancelled) return;
        // Handle the API response structure
        let params: any[] = [];

        // The response should be the data array directly (based on getApiCall implementation)
        // But handle both cases: direct array or wrapped in object
        let dataArray: any[] = [];

        if (Array.isArray(response)) {
          dataArray = response;
        } else if (response && typeof response === 'object') {
          // If response is wrapped, extract data array
          dataArray =
            response.data || response.parameters || response.params || response.items || [];
        }

        // Map the data array to our format
        params = dataArray
          .map((param: any) => {
            if (typeof param === 'string') {
              return {
                parameter: param,
                value: param,
                checked: !isEditMode,
                useTemplateBraces: true,
              };
            } else if (typeof param === 'object' && param !== null) {
              // Handle the API structure: { id, heading, parameter_name }
              // Use parameter_name as the parameter name, and parameter_name as the value (or heading as fallback)
              const paramName =
                param.parameter_name ||
                param.heading ||
                param.parameter ||
                param.name ||
                param.key ||
                '';
              // For value, use parameter_name (which represents the variable name like {campaign_id})
              const paramValue =
                param.parameter_name ||
                param.heading ||
                param.value ||
                param.parameter ||
                param.name ||
                '';
              return {
                parameter: paramName,
                value: paramValue,
                checked: param.checked !== undefined ? param.checked : !isEditMode,
                useTemplateBraces: true,
              };
            }
            return null;
          })
          .filter(Boolean);

        setBodyParams(params);

        setLoadingParams(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error('Error fetching request parameters:', err);
        toast.error('Failed to load request parameters');
        setLoadingParams(false);
      });
    fetchAndSetFlashMessage(
      (msg: any) =>
        msg.module_name === MODULE_ACL.WEBHOOK &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.ADD &&
        msg.type === 'Flash Message',
      setAlertMessage,
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch webhook data for edit mode
  useEffect(() => {
    // Same StrictMode/remount double-invoke guard as the request-parameters effect above.
    let cancelled = false;
    if (isEditMode && id) {
      setLoadingWebhook(true);
      const url = API_WEBHOOK_GET_ONE.replace(':id', id);
      getApiCall(url, {})
        .then((resp: any) => {
          if (cancelled) return;
          // getApiCall already returns response.data.data
          // Handle response - could be array or single object or Sequelize model
          let webhook = resp;
          if (Array.isArray(resp) && resp.length > 0) {
            webhook = resp[0];
          }

          if (webhook) {
            // Extract data from Sequelize model if needed
            const webhookDataObj = webhook.dataValues || webhook;

            // Handle mark_as_default - could be 1, 0, true, false, "1", "0", or null/undefined
            const markAsDefault = webhookDataObj.mark_as_default;
            // Convert to boolean - handle all possible formats
            const isDefault =
              markAsDefault === 1 ||
              markAsDefault === true ||
              markAsDefault === '1' ||
              (markAsDefault !== null &&
                markAsDefault !== undefined &&
                String(markAsDefault) === '1');

            setWebhookData({
              webhook_name: webhookDataObj.webhook_name || '',
              url: webhookDataObj.url || '',
              event:
                webhookDataObj.event_id === 1
                  ? 'HANGUP'
                  : webhookDataObj.event_id === 2
                  ? 'DTMF'
                  : 'CONNECTED_CALLS',
              is_default: isDefault,
            });

            // Parse and set headers
            try {
              const headerData =
                typeof webhookDataObj.header_json === 'string'
                  ? JSON.parse(webhookDataObj.header_json)
                  : webhookDataObj.header_json || {};
              const headerArray = Object.entries(headerData)
                .map(([key, value]: any) => {
                  // Clean header parameter names to remove invalid characters
                  const cleanedKey =
                    key === 'Content-Type' ? key : key.replace(/[^a-zA-Z0-9_-]/g, '');
                  return {
                    parameter: cleanedKey,
                    value: value,
                    protected: key === 'Content-Type',
                  };
                })
                .filter(({ parameter }: any) => parameter.length > 0); // Filter out empty parameters
              setHeaders(
                headerArray.length > 0
                  ? headerArray
                  : [{ parameter: 'Content-Type', value: 'application/json', protected: true }],
              );
            } catch (e) {
              console.error('Error parsing headers:', e);
              setHeaders([
                { parameter: 'Content-Type', value: 'application/json', protected: true },
              ]);
            }

            // Store webhook body data to merge with request parameters
            setWebhookBodyData(webhookDataObj.body_json);
          }
          setLoadingWebhook(false);
        })
        .catch((err: any) => {
          if (cancelled) return;
          console.error('Error fetching webhook:', err);
          toast.error('Failed to load webhook data');
          setLoadingWebhook(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [id, isEditMode]);

  // Reset form when switching from edit mode to create mode
  useEffect(() => {
    // Check if we're on create route (no id)
    const isInCreateMode = !id;
    const wasInEditMode =
      previousIdRef.current !== undefined &&
      previousIdRef.current !== null &&
      previousIdRef.current !== '';

    // If we switched from edit mode to create mode, reset all state
    if (wasInEditMode && isInCreateMode) {
      // Reset all state to initial values when switching from edit to create
      setWebhookData({
        webhook_name: '',
        url: '',
        event: '',
        is_default: false,
      });
      setHeaders([
        { parameter: 'Content-Type', value: 'application/json', protected: true },
      ]);
      setHeaderParam('');
      setHeaderValue('');
      setBodyParam('');
      setBodyValue('');
      setEditingIndex(null);
      setEditParam('');
      setEditingHeaderIndex(null);
      setEditHeaderParam('');
      setEditHeaderValue('');
      setWebhookBodyData(null);
      setHasMergedBodyData(false);
      // Clear all error states
      setWebhookNameError('');
      setWebhookUrlError('');
      setHeaderParamError('');
      setHeaderValueError('');
      setBodyParamError('');
      setBodyValueError('');
      // Reset body params to checked state (for create mode, all should be checked)
      setBodyParams((prevParams: any[]) => prevParams.map((param: any) => ({ ...param, checked: true })));
    }

    // Update the ref to track current id (only update if id actually changed)
    if (previousIdRef.current !== id) {
      previousIdRef.current = id;
    }
  }, [id, location.pathname]);

  // Merge body params when both webhook body data and params are loaded (only once)
  useEffect(() => {
    const shouldMerge = isEditMode && webhookBodyData && !hasMergedBodyData;
    if (shouldMerge && bodyParams.length > 0) {
      try {
        const bodyData =
          typeof webhookBodyData === 'string'
            ? JSON.parse(webhookBodyData)
            : webhookBodyData || {};

        const hasBodyKeys = Object.keys(bodyData).length > 0;
        if (!hasBodyKeys) {
          setHasMergedBodyData(true);
          return;
        }

        // Mark params as checked if they exist in the webhook body
        const paramsWithChecked = bodyParams.map((param: any) => {
          const isChecked = bodyData.hasOwnProperty(param.parameter);
          let value = param.value;
          let useTemplateBraces = param.useTemplateBraces !== false;
          if (
            isChecked &&
            bodyData[param.parameter] !== undefined &&
            bodyData[param.parameter] !== null
          ) {
            const bodyValue = String(bodyData[param.parameter]);
            const trimmed = bodyValue.trim();
            const looksLikeTemplate = /^\{[^}]+\}$/.test(trimmed);
            useTemplateBraces = looksLikeTemplate;
            value = bodyValue.replace(/[{}]/g, '') || param.parameter;
          }
          return {
            ...param,
            checked: isChecked,
            value,
            useTemplateBraces,
          };
        });

        // Also add any params that might be in body but not in the params list
        const bodyParamKeys = Object.keys(bodyData);
        bodyParamKeys.forEach((key) => {
          if (!paramsWithChecked.find((p: any) => p.parameter === key)) {
            const bodyValue = String(bodyData[key]);
            const trimmed = bodyValue.trim();
            const looksLikeTemplate = /^\{[^}]+\}$/.test(trimmed);
            const cleanValue = bodyValue.replace(/[{}]/g, '') || key;
            paramsWithChecked.push({
              parameter: key,
              value: cleanValue,
              checked: true,
              useTemplateBraces: looksLikeTemplate,
            });
          }
        });

        setBodyParams(paramsWithChecked);
        setHasMergedBodyData(true);
      } catch (e) {
        console.error('Error merging body params:', e);
        setHasMergedBodyData(true);
      }
    }
    // Note: bodyParams.length is intentionally included so this re-runs once the
    // request-parameters fetch resolves — webhookBodyData (webhook API) and bodyParams
    // (request-parameters API) load from separate calls and can resolve in either order.
    // hasMergedBodyData guards against re-merging once it has run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhookBodyData, isEditMode, hasMergedBodyData, bodyParams.length]);

  const handleWebhookChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setWebhookData((prev: any) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Real-time validation for webhook name
    if (name === 'webhook_name') {
      const error = validateWebhookName(value);
      setWebhookNameError(error || '');
    }

    // Real-time validation for webhook URL
    if (name === 'url') {
      const error = validateWebhookUrl(value);
      setWebhookUrlError(error || '');
    }
  };

  // Validation functions
  const validateWebhookName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) {
      return 'Webhook name is required';
    }
    if (trimmed.length < 3) {
      return 'Webhook name must be at least 3 characters long';
    }
    if (trimmed.length > 50) {
      return 'Webhook name must not exceed 50 characters';
    }
    // Allow: letters, numbers, spaces, hyphens, underscores
    const nameRegex = /^[a-zA-Z0-9\s\-_]+$/;
    if (!nameRegex.test(trimmed)) {
      return 'Webhook name can only contain letters, numbers, spaces, hyphens, and underscores.';
    }
    return null;
  };

  const validateWebhookUrl = (url: string) => {
    const trimmed = url.trim();
    if (!trimmed) {
      return 'Webhook URL is required';
    }
    // Check for spaces
    if (trimmed.includes(' ')) {
      return 'Webhook URL cannot contain spaces';
    }
    // Check for javascript: protocol
    if (trimmed.toLowerCase().startsWith('javascript:')) {
      return 'JavaScript URLs are not allowed';
    }
    // Must start with http:// or https://
    if (!trimmed.match(/^https?:\/\//i)) {
      return 'Webhook URL must start with http:// or https://';
    }
    // Disallow localhost in production
    if (
      import.meta.env.MODE === 'production' &&
      trimmed.toLowerCase().includes('localhost')
    ) {
      return 'Localhost URLs are not allowed in production';
    }
    // Validate URL format
    try {
      new URL(trimmed);
    } catch (e) {
      return 'Please enter a valid Webhook URL';
    }
    return null;
  };

  const validateHeaderParameter = (param: string) => {
    const trimmed = param.trim();
    if (!trimmed) {
      return 'Header parameter is required';
    }
    if (trimmed.length < 1) {
      return 'Header parameter must be at least 1 character';
    }
    if (trimmed.length > 50) {
      return 'Header parameter must not exceed 50 characters';
    }
    // Allow: letters, numbers, underscores, hyphens
    const paramRegex = /^[a-zA-Z0-9_-]+$/;
    if (!paramRegex.test(trimmed)) {
      return 'Header parameter can only contain letters, numbers, underscores, and hyphens';
    }
    return null;
  };

  const validateBodyParameter = (param: string) => {
    const trimmed = param.trim();
    if (!trimmed) return 'Body parameter is required';
    const paramRegex = /^[a-z0-9_]+$/;
    if (!paramRegex.test(trimmed)) {
      return 'Body parameter can only contain lowercase letters, numbers and underscore (_). Special characters are not allowed.';
    }
    return null;
  };

  const validateJSON = (jsonString: string) => {
    try {
      JSON.parse(jsonString);
      return null;
    } catch (e) {
      return 'Invalid JSON format';
    }
  };

  // Headers functions
  const handleAddHeader = () => {
    // Real-time validation
    const paramTrimmed = headerParam.trim();
    const valueTrimmed = headerValue.trim();

    if (!paramTrimmed) {
      setHeaderParamError('Header parameter is required');
      return;
    } else {
      setHeaderParamError('');
    }

    if (!valueTrimmed) {
      setHeaderValueError('Header value is required');
      return;
    } else {
      setHeaderValueError('');
    }

    // Clean the parameter to remove any invalid characters that might have been entered
    const cleanedParam = paramTrimmed.replace(/[^a-zA-Z0-9_-]/g, '');

    if (!cleanedParam) {
      setHeaderParamError(
        'Header parameter must contain at least one valid character (letters, numbers, underscores, or hyphens)',
      );
      return;
    }

    // Validate header parameter
    const paramError = validateHeaderParameter(cleanedParam);
    if (paramError) {
      setHeaderParamError(paramError);
      return;
    }

    // Check if parameter already exists
    if (
      headers.some(
        (h: any) =>
          h.parameter.toLowerCase() === cleanedParam.toLowerCase() && !h.protected,
      )
    ) {
      setHeaderParamError('Header parameter already exists');
      return;
    }

    setHeaders([...headers, { parameter: cleanedParam, value: valueTrimmed, protected: false }]);
    setHeaderParam('');
    setHeaderValue('');
    setHeaderParamError('');
    setHeaderValueError('');
  };

  const handleEditHeader = (index: number) => {
    const header = headers[index];
    if (header.protected) return;
    setEditingHeaderIndex(index);
    setEditHeaderParam(header.parameter);
    setEditHeaderValue(header.value);
  };

  const handleSaveHeaderEdit = () => {
    const paramTrimmed = editHeaderParam.trim();
    const valueTrimmed = editHeaderValue.trim();

    if (!paramTrimmed) {
      toast.error('Header parameter is required');
      return;
    }

    if (!valueTrimmed) {
      toast.error('Header value is required');
      return;
    }

    // Clean the parameter
    const cleanedParam = paramTrimmed.replace(/[^a-zA-Z0-9_-]/g, '');

    if (!cleanedParam) {
      toast.error(
        'Header parameter must contain at least one valid character (letters, numbers, underscores, or hyphens)',
      );
      return;
    }

    // Validate header parameter
    const paramError = validateHeaderParameter(cleanedParam);
    if (paramError) {
      toast.error(paramError);
      return;
    }

    // Check if parameter already exists (excluding current index)
    if (
      headers.some(
        (h: any, i: number) =>
          i !== editingHeaderIndex &&
          h.parameter.toLowerCase() === cleanedParam.toLowerCase() &&
          !h.protected,
      )
    ) {
      toast.warning('Header parameter already exists');
      return;
    }

    const updatedHeaders = [...headers];
    updatedHeaders[editingHeaderIndex] = {
      parameter: cleanedParam,
      value: valueTrimmed,
      protected: headers[editingHeaderIndex].protected,
    };
    setHeaders(updatedHeaders);
    setEditingHeaderIndex(null);
    setEditHeaderParam('');
    setEditHeaderValue('');
  };

  const handleCancelHeaderEdit = () => {
    setEditingHeaderIndex(null);
    setEditHeaderParam('');
    setEditHeaderValue('');
  };

  const handleDeleteHeader = (index: number) => {
    if (headers[index].protected) return;
    setHeaders(headers.filter((_: any, i: number) => i !== index));
  };

  const copyHeadersJSON = () => {
    const headersObj = headers.reduce((acc: any, h: any) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});
    navigator.clipboard.writeText(JSON.stringify(headersObj, null, 2));
    toast.success('Headers JSON copied to clipboard');
  };

  // Body functions
  const handleAddBodyParam = () => {
    const param = bodyParam.trim();
    const value = bodyValue.trim();

    // Real-time validation
    if (!param) {
      setBodyParamError('Body parameter is required');
      return;
    } else {
      setBodyParamError('');
    }

    if (!value) {
      setBodyValueError('Body value is required');
      return;
    } else {
      setBodyValueError('');
    }

    if (value === '0' || value === 'false') {
      setBodyValueError('Falsy values are not allowed in body parameters');
      return;
    }

    const paramError = validateBodyParameter(param);
    if (paramError) {
      setBodyParamError(paramError);
      return;
    }

    if (bodyParams.some((p: any) => p.parameter.toLowerCase() === param.toLowerCase())) {
      setBodyParamError('Body parameter already exists');
      return;
    }

    setBodyParams([
      ...bodyParams,
      { parameter: param, value: value, checked: true, useTemplateBraces: false },
    ]);

    setBodyParam('');
    setBodyValue('');
    setBodyParamError('');
    setBodyValueError('');
  };

  const handleToggleBodyParam = (index: number) => {
    const updatedParams = [...bodyParams];
    updatedParams[index].checked = !updatedParams[index].checked;
    setBodyParams(updatedParams);
  };

  const handleToggleAllBodyParams = (checked: boolean) => {
    const updatedParams = bodyParams.map((param: any) => ({
      ...param,
      checked: checked,
    }));
    setBodyParams(updatedParams);
  };

  const handleEditBodyParam = (index: number) => {
    const param = bodyParams[index];
    // Validate the body parameter before allowing edit
    const paramError = validateBodyParameter(param.parameter);
    if (paramError) {
      toast.warning(
        `Current body parameter has validation issues: ${paramError}. Please fix it.`,
      );
    }
    setEditingIndex(index);
    setEditParam(param.parameter);
  };

  const handleSaveEdit = () => {
    const param = editParam.trim();
    const originalRow = bodyParams[editingIndex];
    const originalValue = (originalRow?.value ?? '').trim();

    if (!param) {
      toast.warning('Parameter is required.');
      return;
    }

    if (!originalValue || originalValue === '0' || originalValue === 'false') {
      toast.warning('Value is missing or invalid for this row. Falsy values are not allowed.');
      return;
    }

    const paramError = validateBodyParameter(param);
    if (paramError) {
      toast.error(paramError);
      return;
    }

    if (
      bodyParams.some(
        (p: any, i: number) =>
          i !== editingIndex && p.parameter.toLowerCase() === param.toLowerCase(),
      )
    ) {
      toast.error('Body parameter already exists');
      return;
    }

    const updatedParams = [...bodyParams];
    updatedParams[editingIndex] = {
      parameter: param,
      value: originalValue,
      checked: updatedParams[editingIndex].checked,
      useTemplateBraces: originalRow.useTemplateBraces,
    };

    setBodyParams(updatedParams);
    setEditingIndex(null);
    setEditParam('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditParam('');
  };

  const bodyValueToStoredJson = (p: any) => {
    if (p.useTemplateBraces === false) {
      return String(p.value ?? '');
    }
    return `{${p.value}}`;
  };

  const copyBodyJSON = () => {
    const bodyObj = bodyParams
      .filter((p: any) => p.checked)
      .reduce((acc: any, p: any) => {
        acc[p.parameter] = bodyValueToStoredJson(p);
        return acc;
      }, {});
    navigator.clipboard.writeText(JSON.stringify(bodyObj, null, 2));
    toast.success('Body JSON copied to clipboard');
  };

  // Generate JSON previews
  const getHeadersJSON = () => {
    const headersObj = headers.reduce((acc: any, h: any) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});
    return JSON.stringify(headersObj, null, 2);
  };

  const getBodyJSON = () => {
    const bodyObj = bodyParams
      .filter((p: any) => p.checked)
      .reduce((acc: any, p: any) => {
        acc[p.parameter] = bodyValueToStoredJson(p);
        return acc;
      }, {});
    return JSON.stringify(bodyObj, null, 2);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    // Validate webhook name
    const nameError = validateWebhookName(webhookData.webhook_name);
    if (nameError) {
      toast.error(nameError);
      return;
    }

    // Validate webhook URL
    const urlError = validateWebhookUrl(webhookData.url);
    if (urlError) {
      toast.error(urlError);
      return;
    }

    // Validate event
    if (!webhookData.event) {
      toast.error('Please select an event');
      return;
    }
    const allowedEvents = ['HANGUP', 'DTMF', 'CONNECTED_CALLS'];
    if (!allowedEvents.includes(webhookData.event)) {
      toast.error('Please select a valid event');
      return;
    }

    // Validate headers JSON
    const headersJSON = getHeadersJSON();
    const headersError = validateJSON(headersJSON);
    if (headersError) {
      toast.error(`Headers JSON is invalid: ${headersError}`);
      return;
    }

    // Clean and validate each header parameter
    const cleanedHeaders = headers
      .map((header: any) => {
        if (header.protected) return header;
        const cleanedParam = header.parameter.replace(/[^a-zA-Z0-9_-]/g, '');
        if (cleanedParam !== header.parameter && cleanedParam.length > 0) {
          toast.warning(
            `Header parameter "${header.parameter}" contains invalid characters and will be saved as "${cleanedParam}"`,
          );
        }
        return {
          ...header,
          parameter: cleanedParam || header.parameter,
        };
      })
      .filter((header: any) => header.parameter.length > 0 || header.protected);

    // Validate each cleaned header parameter
    for (const header of cleanedHeaders) {
      const paramError = validateHeaderParameter(header.parameter);
      if (paramError && !header.protected) {
        toast.error(`Header parameter "${header.parameter}": ${paramError}`);
        return;
      }
    }

    // Validate body JSON
    const bodyJSON = getBodyJSON();
    const bodyError = validateJSON(bodyJSON);
    if (bodyError) {
      toast.error(`Body JSON is invalid: ${bodyError}`);
      return;
    }

    // Validate each body parameter
    const checkedBodyParams = bodyParams.filter((p: any) => p.checked);
    for (const param of checkedBodyParams) {
      const paramError = validateBodyParameter(param.parameter);
      if (paramError) {
        toast.error(`Body parameter "${param.parameter}": ${paramError}`);
        return;
      }
    }

    setLoading(true);

    // Prepare headers object (include all cleaned headers for storage)
    const allHeaders = cleanedHeaders.reduce((acc: any, h: any) => {
      acc[h.parameter] = h.value;
      return acc;
    }, {});

    // Prepare body object (only include checked parameters).
    // Default/API rows use "{value}" placeholders; user-added rows use plain string values.
    const bodyObj = bodyParams
      .filter((p: any) => p.checked && p.parameter && p.value)
      .reduce((acc: any, p: any) => {
        acc[p.parameter] = bodyValueToStoredJson(p);
        return acc;
      }, {});

    // Prepare payload according to backend schema
    const payload = {
      webhook_name: webhookData.webhook_name.trim(),
      url: webhookData.url.trim(),
      event_id:
        webhookData.event === 'HANGUP' ? 1 : webhookData.event === 'DTMF' ? 2 : 3,
      mark_as_default: webhookData.is_default ? 1 : 0, // INTEGER, not string
      header_json: JSON.stringify(allHeaders), // JSON string (includes all headers)
      body_json: JSON.stringify(bodyObj), // JSON string
    };

    const apiCall = isEditMode
      ? postApiCall(API_WEBHOOK_UPDATE.replace(':id', id), payload)
      : postApiCall(API_WEBHOOK_CREATE, payload);

    apiCall
      .then((resp: any) => {
        toast.success(
          isEditMode ? 'Webhook updated successfully!' : 'Webhook created successfully!',
        );
        setTimeout(() => {
          navigate(`/${ROUTES.MANAGE_WEBHOOK.LIST}`);
        }, 2000);
      })
      .catch((err: any) => {
        console.log('err', err);
        toast.error(friendlyApiError(err) || 'Failed to save webhook');
        setLoading(false);
      });
  };

  if (loadingWebhook) return <Loader />;

  return (
    <ScrollPage>
      {alertMessage && (
        <div
          className="webhook-marquee mb-4 overflow-hidden rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-700 dark:text-emerald-400"
          role="status"
        >
          <div
            className="webhook-marquee__track"
            style={{ animationDuration: `${String(alertMessage).length * 0.2 + 15}s` }}
          >
            <span className="webhook-marquee__item">{alertMessage}</span>
            <span className="webhook-marquee__item" aria-hidden="true">
              {alertMessage}
            </span>
          </div>
          <style>{`
            .webhook-marquee__track {
              display: flex;
              width: max-content;
              white-space: nowrap;
              animation-name: webhookMarquee;
              animation-timing-function: linear;
              animation-iteration-count: infinite;
            }
            .webhook-marquee:hover .webhook-marquee__track {
              animation-play-state: paused;
            }
            .webhook-marquee__item {
              padding-right: 3rem;
            }
            @keyframes webhookMarquee {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }
          `}</style>
        </div>
      )}
      <PageHeader
        title={isEditMode ? 'Edit Webhook' : 'Add Webhook'}
        description="Configure headers, body, and event trigger for this webhook."
        actions={
          <ShadPageButton asChild variant="outline">
            <Link to={`/${ROUTES.MANAGE_WEBHOOK.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </ShadPageButton>
        }
      />
      <div>
        <Row className="mx-0">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  {/* Webhook Details Section */}
                  <div className="mb-4">
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Webhook Name{' '}
                            <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="webhook_name"
                            value={webhookData.webhook_name}
                            onChange={handleWebhookChange}
                            onBlur={(e) => {
                              const error = validateWebhookName(e.target.value);
                              setWebhookNameError(error || '');
                            }}
                            placeholder="Webhook Name"
                            required
                            minLength={3}
                            maxLength={50}
                            pattern="[a-zA-Z0-9\s\-_]+"
                            title="Webhook name can only contain letters, numbers, spaces, hyphens, and underscores."
                            isInvalid={!!webhookNameError}
                          />
                          {webhookNameError && (
                            <Form.Control.Feedback
                              type="invalid"
                              style={{ display: 'block' }}
                            >
                              {webhookNameError}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Webhook URL
                            <span style={{ color: 'red', margin: '0 4px' }}>*</span>
                          </Form.Label>
                          <Form.Control
                            type="url"
                            name="url"
                            value={webhookData.url}
                            onChange={handleWebhookChange}
                            onBlur={(e) => {
                              const error = validateWebhookUrl(e.target.value);
                              setWebhookUrlError(error || '');
                            }}
                            placeholder="Webhook URL"
                            required
                            title="Must start with http:// or https://"
                            isInvalid={!!webhookUrlError}
                          />
                          {webhookUrlError && (
                            <Form.Control.Feedback
                              type="invalid"
                              style={{ display: 'block' }}
                            >
                              {webhookUrlError}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Event<span style={{ color: 'red', margin: '0 4px' }}>*</span>
                          </Form.Label>
                          <Form.Select
                            name="event"
                            value={webhookData.event}
                            onChange={handleWebhookChange}
                            required
                          >
                            <option value="">Select Events</option>
                            <option value="HANGUP">HANGUP</option>
                            <option value="DTMF">DTMF</option>
                            <option value="CONNECTED_CALLS">CONNECTED_CALLS</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>&nbsp;</Form.Label>
                          <Form.Check
                            type="checkbox"
                            name="is_default"
                            label="Make this as default"
                            checked={webhookData.is_default}
                            onChange={handleWebhookChange}
                            className="mt-2"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Headers Section */}
                  <div className="mb-4">
                    <h5 className="mb-3">Headers</h5>
                    <Row>
                      <Col md={8}>
                        <Row className="mb-3">
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Parameter"
                              value={headerParam}
                              onChange={(e) => {
                                const value = e.target.value;
                                setHeaderParam(value);
                                if (!value.trim()) {
                                  setHeaderParamError('Header parameter is required');
                                  return;
                                }
                                const invalidCharRegex = /[^a-zA-Z0-9_-]/;
                                if (invalidCharRegex.test(value)) {
                                  setHeaderParamError(
                                    'Only letters, numbers, underscores (_) and hyphens (-) are allowed',
                                  );
                                  return;
                                }
                                if (value.length > 50) {
                                  setHeaderParamError(
                                    'Header parameter cannot exceed 50 characters',
                                  );
                                  return;
                                }
                                const error = validateHeaderParameter(value);
                                setHeaderParamError(error || '');
                              }}
                              onBlur={(e) => {
                                if (!e.target.value.trim()) {
                                  setHeaderParamError('Header parameter is required');
                                }
                              }}
                              maxLength={50}
                              isInvalid={!!headerParamError}
                            />
                            {headerParamError && (
                              <Form.Control.Feedback
                                type="invalid"
                                style={{ display: 'block', fontSize: '0.875rem' }}
                              >
                                {headerParamError}
                              </Form.Control.Feedback>
                            )}
                          </Col>
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Value"
                              value={headerValue}
                              onChange={(e) => {
                                setHeaderValue(e.target.value);
                                // Real-time validation
                                if (!e.target.value.trim()) {
                                  setHeaderValueError('Header value is required');
                                } else {
                                  setHeaderValueError('');
                                }
                              }}
                              onBlur={(e) => {
                                if (!e.target.value.trim()) {
                                  setHeaderValueError('Header value is required');
                                }
                              }}
                              isInvalid={!!headerValueError}
                            />
                            {headerValueError && (
                              <Form.Control.Feedback
                                type="invalid"
                                style={{ display: 'block', fontSize: '0.875rem' }}
                              >
                                {headerValueError}
                              </Form.Control.Feedback>
                            )}
                          </Col>
                          <Col md={2}>
                            <button
                              type="button"
                              onClick={handleAddHeader}
                              title="Add header"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-md transition hover:brightness-105"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </Col>
                        </Row>

                        <div className="overflow-x-auto rounded-lg border border-border/60 bg-card">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-primary/[0.06] text-[10px] uppercase tracking-wide text-muted-foreground">
                              <th className="px-3 py-2 text-left font-semibold">Parameter</th>
                              <th className="px-3 py-2 text-left font-semibold">Value</th>
                              <th className="px-3 py-2 text-left font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {headers.map((header: any, index: number) => (
                              <tr
                                key={index}
                                className={`border-t border-border/40 ${index % 2 ? 'bg-muted/30' : ''}`}
                              >
                                <td className="px-3 py-2 font-medium text-foreground">
                                  {editingHeaderIndex === index ? (
                                    <input
                                      type="text"
                                      value={editHeaderParam}
                                      onChange={(e) => {
                                        const value = e.target.value.replace(
                                          /[^a-zA-Z0-9_-]/g,
                                          '',
                                        );
                                        setEditHeaderParam(value);
                                      }}
                                      className="input-embossed h-8 w-full min-w-[150px] rounded-md px-2 text-xs"
                                      maxLength={50}
                                      pattern="[a-zA-Z0-9_\-]+"
                                    />
                                  ) : (
                                    <span>{header.parameter}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-foreground">
                                  {editingHeaderIndex === index ? (
                                    <input
                                      type="text"
                                      value={editHeaderValue}
                                      onChange={(e) => setEditHeaderValue(e.target.value)}
                                      className="input-embossed h-8 w-full min-w-[150px] rounded-md px-2 text-xs"
                                    />
                                  ) : (
                                    <span>{header.value}</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {header.protected ? (
                                    <span className="text-muted-foreground">Protected</span>
                                  ) : editingHeaderIndex === index ? (
                                    <div className="d-flex gap-2">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={handleSaveHeaderEdit}
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleCancelHeaderEdit}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5">
                                      <button
                                        type="button"
                                        title="Edit header"
                                        onClick={() => handleEditHeader(index)}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        title="Delete header"
                                        onClick={() => handleDeleteHeader(index)}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="flex items-center justify-between border-b border-border/50 pb-2 mb-2">
                          <h6 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground m-0">
                            Headers JSON
                          </h6>
                          <button
                            type="button"
                            onClick={copyHeadersJSON}
                            title="Copy to clipboard"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                        <textarea
                          rows={8}
                          value={getHeadersJSON()}
                          readOnly
                          className="input-embossed w-full rounded-md p-3 font-mono text-xs text-foreground"
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Body Section */}
                  <div className="mb-4">
                    <h5 className="mb-3">Body</h5>
                    <Row>
                      <Col md={8}>
                        <Row className="mb-3">
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Parameter"
                              value={bodyParam}
                              onChange={(e) => {
                                const value = e.target.value;
                                setBodyParam(value);
                                if (!value.trim()) {
                                  setBodyParamError('Body parameter is required');
                                  return;
                                }
                                const invalidCharRegex = /[^a-z0-9_]/;
                                if (invalidCharRegex.test(value)) {
                                  setBodyParamError(
                                    'Only lowercase letters, numbers, and underscores (_) are allowed',
                                  );
                                  return;
                                }
                                setBodyParamError('');
                              }}
                              onBlur={(e) => {
                                if (!e.target.value.trim()) {
                                  setBodyParamError('Body parameter is required');
                                }
                              }}
                              isInvalid={!!bodyParamError}
                            />
                            {bodyParamError && (
                              <Form.Control.Feedback
                                type="invalid"
                                style={{ display: 'block', fontSize: '0.875rem' }}
                              >
                                {bodyParamError}
                              </Form.Control.Feedback>
                            )}
                          </Col>
                          <Col md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Value"
                              value={bodyValue}
                              onChange={(e) => {
                                setBodyValue(e.target.value);
                                // Real-time validation
                                if (!e.target.value.trim()) {
                                  setBodyValueError('Body value is required');
                                } else if (
                                  e.target.value.trim() === '0' ||
                                  e.target.value.trim() === 'false'
                                ) {
                                  setBodyValueError(
                                    'Falsy values are not allowed in body parameters',
                                  );
                                } else {
                                  setBodyValueError('');
                                }
                              }}
                              onBlur={(e) => {
                                if (!e.target.value.trim()) {
                                  setBodyValueError('Body value is required');
                                } else if (
                                  e.target.value.trim() === '0' ||
                                  e.target.value.trim() === 'false'
                                ) {
                                  setBodyValueError(
                                    'Falsy values are not allowed in body parameters',
                                  );
                                }
                              }}
                              isInvalid={!!bodyValueError}
                            />
                            {bodyValueError && (
                              <Form.Control.Feedback
                                type="invalid"
                                style={{ display: 'block', fontSize: '0.875rem' }}
                              >
                                {bodyValueError}
                              </Form.Control.Feedback>
                            )}
                          </Col>
                          <Col md={2}>
                            <button
                              type="button"
                              onClick={handleAddBodyParam}
                              title="Add body parameter"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-md transition hover:brightness-105"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </Col>
                        </Row>

                        <div className="max-h-[240px] overflow-y-auto rounded-lg border border-border/60 bg-card">
                          <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase tracking-wide text-muted-foreground">
                              <tr>
                                <th className="sticky top-0 z-10 w-10 border-b border-border/60 bg-slate-100 px-3 py-2 text-left font-semibold dark:bg-slate-800">
                                  <Form.Check
                                    type="checkbox"
                                    checked={
                                      bodyParams.length > 0 &&
                                      bodyParams.every((p: any) => p.checked)
                                    }
                                    onChange={(e) => handleToggleAllBodyParams(e.target.checked)}
                                  />
                                </th>
                                <th className="sticky top-0 z-10 border-b border-border/60 bg-slate-100 px-3 py-2 text-left font-semibold dark:bg-slate-800">
                                  Parameter
                                </th>
                                <th className="sticky top-0 z-10 border-b border-border/60 bg-slate-100 px-3 py-2 text-left font-semibold dark:bg-slate-800">
                                  Value
                                </th>
                                <th className="sticky top-0 z-10 border-b border-border/60 bg-slate-100 px-3 py-2 text-left font-semibold dark:bg-slate-800">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadingParams ? (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-3 py-4 text-center text-muted-foreground"
                                  >
                                    <span className="inline-flex items-center gap-1.5">
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Loading parameters…
                                    </span>
                                  </td>
                                </tr>
                              ) : bodyParams.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="px-3 py-6 text-center text-muted-foreground"
                                  >
                                    No parameters available
                                  </td>
                                </tr>
                              ) : (
                                bodyParams.map((param: any, index: number) => (
                                  <tr
                                    key={index}
                                    className={`border-t border-border/40 ${index % 2 ? 'bg-muted/30' : ''}`}
                                  >
                                    <td className="px-3 py-2">
                                      <Form.Check
                                        type="checkbox"
                                        checked={!!param.checked}
                                        onChange={() => handleToggleBodyParam(index)}
                                      />
                                    </td>
                                    <td className="px-3 py-2 font-medium text-foreground">
                                      {editingIndex === index ? (
                                        <input
                                          type="text"
                                          value={editParam}
                                          onChange={(e) => {
                                            const value = e.target.value
                                              .toLowerCase()
                                              .replace(/[^a-z0-9_]/g, '');
                                            setEditParam(value);
                                          }}
                                          className="input-embossed h-8 w-full min-w-[150px] rounded-md px-2 text-xs"
                                          pattern="[a-z0-9_]+"
                                          title="Body parameter can only contain lowercase letters, numbers, and underscores (no spaces)"
                                        />
                                      ) : (
                                        <span
                                          className={editingIndex === index ? 'text-primary' : ''}
                                        >
                                          {param.parameter || ''}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-foreground">
                                      <span>{param.value || ''}</span>
                                    </td>
                                    <td className="px-3 py-2">
                                      {editingIndex === index ? (
                                        <div className="flex items-center gap-1.5">
                                          <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleSaveEdit}
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={handleCancelEdit}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          title="Edit parameter"
                                          onClick={() => handleEditBodyParam(index)}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="flex items-center justify-between border-b border-border/50 pb-2 mb-2">
                          <h6 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground m-0">
                            Selected Body JSON
                          </h6>
                          <button
                            type="button"
                            onClick={copyBodyJSON}
                            title="Copy to clipboard"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                        <textarea
                          rows={15}
                          value={getBodyJSON()}
                          readOnly
                          className="input-embossed w-full rounded-md p-3 font-mono text-xs text-foreground"
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6 flex items-center justify-end gap-2 border-t border-border/50 pt-4">
                    <ShadPageButton asChild variant="outline">
                      <Link to={`/${ROUTES.MANAGE_WEBHOOK.LIST}`}>Cancel</Link>
                    </ShadPageButton>
                    <ShadPageButton
                      type="submit"
                      disabled={loading || loadingParams}
                      className="gap-1.5"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {loading
                        ? isEditMode
                          ? 'Updating…'
                          : 'Adding…'
                        : isEditMode
                          ? 'Update webhook'
                          : 'Add webhook'}
                    </ShadPageButton>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </ScrollPage>
  );
};

export default CreateManageWebhook;
